import { useEffect, useState } from 'react';
import { cloudApi } from '../services/api';
import {
  CLOUD_CONNECT_NEXT_STEP,
  CLOUD_FOLDER_PLACEHOLDER,
  CLOUD_FOLDER_CREATE_HELPER,
  CLOUD_OAUTH_STUB_NOTE,
  CLOUD_PROVIDERS,
  CLOUD_STORAGE_FIELD_LABEL,
  buildCloudStorageMeta,
  isLiveCloudProvider,
  isGoogleCloudProvider,
  isStubCloudProvider,
  normalizeCloudProvider,
  type CloudProviderValue,
  type CloudStorageMeta,
} from '../lib/cloudStorage';
import './FileStoragePanel.css';

const STORAGE_PREFIX = 'mentology.cloudStorage.';

export function loadCloudStorageIntent(storageKey: string): CloudStorageMeta {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (!raw) return buildCloudStorageMeta({ provider: 'pending', folder_label: '' });
    const parsed = JSON.parse(raw) as Partial<CloudStorageMeta>;
    return buildCloudStorageMeta(parsed);
  } catch {
    return buildCloudStorageMeta({ provider: 'pending', folder_label: '' });
  }
}

export function saveCloudStorageIntent(storageKey: string, meta: CloudStorageMeta) {
  localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(meta));
}

interface FileStoragePanelProps {
  storageKey: string;
  scope?: 'user' | 'org';
  returnTo?: string;
  /** Optional: also push metadata to an API (e.g. user bio / org cloud). */
  onPersist?: (meta: CloudStorageMeta) => Promise<void> | void;
  compact?: boolean;
  defaultOpen?: boolean;
  folderPlaceholder?: string;
  folderLabelHeading?: string;
  /** Called before leaving the page for Google OAuth so Overview fields are not lost. */
  onBeforeGoogleConnect?: () => Promise<void> | void;
}

export default function FileStoragePanel({
  storageKey,
  scope = 'user',
  returnTo,
  onPersist,
  compact = false,
  defaultOpen = false,
  folderPlaceholder = CLOUD_FOLDER_PLACEHOLDER,
  folderLabelHeading = 'Business files folder label',
  onBeforeGoogleConnect,
}: FileStoragePanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [provider, setProvider] = useState<CloudProviderValue>('pending');
  const [folderLabel, setFolderLabel] = useState('');
  const [folderId, setFolderId] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const refreshStatus = async () => {
    try {
      const data = await cloudApi.status(scope);
      const cloud = buildCloudStorageMeta(data.cloud_storage || {});
      setProvider(cloud.provider);
      setFolderLabel(cloud.folder_label);
      setFolderId(cloud.folder_id || null);
      setConnected(Boolean(cloud.connected));
      saveCloudStorageIntent(storageKey, cloud);
    } catch {
      const saved = loadCloudStorageIntent(storageKey);
      setProvider(saved.provider);
      setFolderLabel(saved.folder_label);
      setFolderId(saved.folder_id || null);
      setConnected(Boolean(saved.connected));
    }
  };

  useEffect(() => {
    void refreshStatus();
  }, [storageKey, scope]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cloud = params.get('cloud');
    if (!cloud) return;
    if (cloud === 'connected') setStatus('Google Drive connected. Your nominated folder is ready.');
    if (cloud === 'denied') setStatus('Google authorization was denied.');
    if (cloud === 'error' || cloud === 'invalid_state') {
      setStatus('Google connection failed. Try Connect Google Drive again.');
    }
    void refreshStatus();
    params.delete('cloud');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);

  const handleSaveIntent = async () => {
    setSaving(true);
    setStatus('');
    try {
      if (isGoogleCloudProvider(provider) && connected) {
        const label = folderLabel.trim() || folderPlaceholder.replace(/^e\.g\.\s*/i, '');
        const data = await cloudApi.saveFolder({
          scope,
          folder_label: label,
        });
        const cloud = buildCloudStorageMeta(data.cloud_storage || {});
        saveCloudStorageIntent(storageKey, cloud);
        await onPersist?.(cloud);
        setProvider(cloud.provider);
        setFolderLabel(cloud.folder_label);
        setFolderId(cloud.folder_id || null);
        setConnected(Boolean(cloud.connected));
        setStatus(`Folder ready in Google Drive: ${cloud.folder_label || label}. Put your documents in it — this does not upload files.`);
        return;
      }

      const meta = buildCloudStorageMeta({
        provider: normalizeCloudProvider(provider),
        folder_label: folderLabel,
        folder_id: isLiveCloudProvider(provider) ? folderId : null,
        connected: isLiveCloudProvider(provider) ? connected : false,
      });
      saveCloudStorageIntent(storageKey, meta);
      await onPersist?.(meta);
      if (isStubCloudProvider(meta.provider)) {
        setStatus('Provider intent saved. Live OAuth for this provider is not registered yet — connected stays false.');
      } else if (meta.provider === 'pending') {
        setStatus('Connect later saved. Setup can continue without cloud access.');
      } else {
        setStatus('Google Drive selected. Use Connect Google Drive — Mentology will create this folder for you.');
      }
      setProvider(meta.provider);
      setFolderLabel(meta.folder_label);
      setFolderId(meta.folder_id || null);
      setConnected(Boolean(meta.connected));
    } catch (error) {
      console.warn('[FileStoragePanel]', error);
      setStatus('Saved locally. Server sync pending.');
    } finally {
      setSaving(false);
    }
  };

  const handleConnectGoogle = async () => {
    setStatus('');
    try {
      await onBeforeGoogleConnect?.();
      const data = await cloudApi.startGoogleOAuth({
        scope,
        returnTo: returnTo || window.location.pathname,
        folderLabel: folderLabel || folderPlaceholder.replace(/^e\.g\.\s*/i, ''),
      });
      if (data.url) {
        const inIframe = typeof window !== 'undefined' && window.self !== window.top;
        if (inIframe && window.top) {
          setStatus('Opening Google Drive…');
          window.top.location.href = data.url;
          return;
        }
        window.location.href = data.url;
        return;
      }
      setStatus('Unable to start Google OAuth.');
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Google Drive is not ready to connect right now. Try again shortly, or set this up later from your Setup page.');
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      const data = await cloudApi.disconnectGoogle(scope);
      const cloud = buildCloudStorageMeta(data.cloud_storage || { provider: 'pending' });
      setProvider(isGoogleCloudProvider(cloud.provider) ? 'pending' : cloud.provider);
      setFolderId(null);
      setConnected(false);
      saveCloudStorageIntent(storageKey, { ...cloud, connected: false, folder_id: null });
      await onPersist?.({ ...cloud, connected: false, folder_id: null, provider: 'pending' });
      setStatus(data.message || 'Google Drive disconnected.');
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Disconnect failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`file-storage-panel ${compact ? 'file-storage-panel-compact' : ''}`}>
      <button
        type="button"
        className="file-storage-toggle btn-primary"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        File Storage {open ? '▲' : '▼'}
      </button>

      {open && (
        <div className="file-storage-body">
          <p className="file-storage-label">{CLOUD_STORAGE_FIELD_LABEL}</p>
          <p className="file-storage-note">{CLOUD_OAUTH_STUB_NOTE}</p>
          <p className="file-storage-helper">
            Status: {connected ? 'Connected' : 'Not connected yet'}
          </p>
          {!connected && (
            <p className="file-storage-helper">{CLOUD_CONNECT_NEXT_STEP}</p>
          )}

          <label className="file-storage-label">
            Choose a provider
            <select
              className="input-field file-storage-input"
              value={provider}
              onChange={(e) => {
                const next = normalizeCloudProvider(e.target.value);
                setProvider(next);
                if (!isGoogleCloudProvider(next)) {
                  setConnected(false);
                  setFolderId(null);
                }
              }}
            >
              {CLOUD_PROVIDERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="file-storage-label">
            {folderLabelHeading}
            <input
              className="input-field file-storage-input"
              type="text"
              value={folderLabel}
              placeholder={folderPlaceholder}
              onChange={(e) => setFolderLabel(e.target.value)}
            />
          </label>

          <p className="file-storage-helper">
            {connected
              ? `Mentology created “${folderLabel || 'your folder'}” in your Google Drive. Put Word/PDF documents in that folder, then use Select General Documents below to tick the files Mentology should use.`
              : CLOUD_FOLDER_CREATE_HELPER}
          </p>

          {isGoogleCloudProvider(provider) && (
            <div className="file-storage-google-actions">
              {!connected ? (
                <button
                  type="button"
                  className="btn-primary file-storage-save"
                  onClick={() => void handleConnectGoogle()}
                  disabled={saving}
                >
                  Connect Google Drive
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary file-storage-save"
                  onClick={() => void handleDisconnect()}
                  disabled={saving}
                >
                  Disconnect Google Drive
                </button>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn-primary file-storage-save"
            onClick={() => void handleSaveIntent()}
            disabled={saving}
          >
            {saving ? 'Saving...' : connected ? 'Save folder name' : 'Save File Storage'}
          </button>
          {status && <p className="file-storage-status" role="status">{status}</p>}
        </div>
      )}
    </div>
  );
}
